import { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
	ChartBarSquareIcon,
	Cog6ToothIcon,
	CurrencyDollarIcon,
	DocumentMagnifyingGlassIcon,
	FolderIcon,
	ServerIcon,
	ShoppingCartIcon,
	SignalIcon,
	UserGroupIcon,
	XMarkIcon,
} from '@heroicons/react/24/outline';
import {
	Bars3Icon,
	CameraIcon,
	ChatBubbleOvalLeftIcon,
	MagnifyingGlassIcon,
} from '@heroicons/react/20/solid';
import Stats from './admin/Stats';
import Table from './admin/Table';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import Logo from '../components/Logo';
import { useUser } from '../context/UserContext';
import SecondaryNavigation from './admin/SecondaryNav';

function classNames(...classes) {
	return classes.filter(Boolean).join(' ');
}

// Shared nav-item classes. Active items get a green-tinted fill + inset ring;
// inactive items use warm ink grays (no blue tint) and hover toward green.
function navItemClass(current) {
	return classNames(
		current
			? 'bg-primary-500/15 text-white ring-1 ring-inset ring-primary-500/30'
			: 'text-ink-300 hover:text-white hover:bg-ink-800',
		'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold transition-colors',
	);
}

function navIconClass(current) {
	return classNames(
		current ? 'text-primary-400' : 'text-ink-400 group-hover:text-primary-400',
		'h-6 w-6 shrink-0 transition-colors',
	);
}

export default function AdminLayout() {
	const { user } = useUser();
	const location = useLocation();
	const navigation = [
		{
			name: 'Home',
			href: '/',
			icon: FolderIcon,
			current: location.pathname === '/',
		},
		{
			name: 'CRM',
			href: '/admin/crm',
			icon: ChartBarSquareIcon,
			current: location.pathname === '/admin/crm',
		},
		{
			name: 'Proposals',
			href: '/admin/proposals',
			icon: CurrencyDollarIcon,
			current: location.pathname.startsWith('/admin/proposals'),
		},
		{
			name: 'Customers',
			href: '/admin/customers',
			icon: ShoppingCartIcon,
			current: location.pathname === '/admin/customers',
		},
		{
			name: 'Messages',
			href: '/admin/messages',
			icon: ChatBubbleOvalLeftIcon,
			current: location.pathname === '/admin/messages',
		},
		{
			name: 'Users',
			href: '/admin/users',
			icon: UserGroupIcon,
			current: location.pathname === '/admin/users',
		},
		{
			name: 'AI Images',
			href: '/admin/images',
			icon: CameraIcon,
			current: location.pathname === '/admin/images',
		},
		{
			name: 'Logs',
			href: '/admin/logs',
			icon: DocumentMagnifyingGlassIcon,
			current: location.pathname === '/admin/logs',
		},
		// { name: 'Usage', href: '#', icon: ChartBarSquareIcon, current: false },
		// { name: 'Settings', href: '#', icon: Cog6ToothIcon, current: false },
	];
	const teams = [
		// { id: 1, name: 'Planetaria', href: '#', initial: 'P', current: false },
		// { id: 2, name: 'Protocol', href: '#', initial: 'P', current: false },
		// { id: 3, name: 'Tailwind Labs', href: '#', initial: 'T', current: false },
	];

	const [sidebarOpen, setSidebarOpen] = useState(false);

	return (
		<>
			<div>
				<Transition.Root
					show={sidebarOpen}
					as={Fragment}
				>
					<Dialog
						as='div'
						className='relative z-50 xl:hidden'
						onClose={setSidebarOpen}
					>
						<Transition.Child
							as={Fragment}
							enter='transition-opacity ease-linear duration-300'
							enterFrom='opacity-0'
							enterTo='opacity-100'
							leave='transition-opacity ease-linear duration-300'
							leaveFrom='opacity-100'
							leaveTo='opacity-0'
						>
							<div className='fixed inset-0 bg-black/80' />
						</Transition.Child>

						<div className='fixed inset-0 flex'>
							<Transition.Child
								as={Fragment}
								enter='transition ease-in-out duration-300 transform'
								enterFrom='-translate-x-full'
								enterTo='translate-x-0'
								leave='transition ease-in-out duration-300 transform'
								leaveFrom='translate-x-0'
								leaveTo='-translate-x-full'
							>
								<Dialog.Panel className='relative mr-16 flex w-full max-w-xs flex-1'>
									<Transition.Child
										as={Fragment}
										enter='ease-in-out duration-300'
										enterFrom='opacity-0'
										enterTo='opacity-100'
										leave='ease-in-out duration-300'
										leaveFrom='opacity-100'
										leaveTo='opacity-0'
									>
										<div className='absolute left-full top-0 flex w-16 justify-center pt-5'>
											<button
												type='button'
												className='-m-2.5 p-2.5'
												onClick={() => setSidebarOpen(false)}
											>
												<span className='sr-only'>Close sidebar</span>
												<XMarkIcon
													className='h-6 w-6 text-white'
													aria-hidden='true'
												/>
											</button>
										</div>
									</Transition.Child>
									{/* Sidebar component, swap this element with another sidebar if you like */}
									<div className='flex grow flex-col gap-y-5 overflow-y-auto bg-black px-6 ring-1 ring-white/10'>
										<div className='flex h-16 shrink-0 items-center'>
											<Logo />
										</div>
										<nav className='flex flex-1 flex-col'>
											<ul
												role='list'
												className='flex flex-1 flex-col gap-y-7'
											>
												<li>
													<ul
														role='list'
														className='-mx-2 space-y-1'
													>
														{navigation.map((item) => (
															<li key={item.name}>
																<NavLink
																	to={item.href}
																	className={navItemClass(item.current)}
																>
																	<item.icon
																		className={navIconClass(item.current)}
																		aria-hidden='true'
																	/>
																	{item.name}
																</NavLink>
															</li>
														))}
													</ul>
												</li>
												<li>
													<div className='text-xs font-semibold leading-6 text-ink-400'>
														Your teams
													</div>
													<ul
														role='list'
														className='-mx-2 mt-2 space-y-1'
													>
														{teams.map((team) => (
															<li key={team.name}>
																<NavLink
																	to={team.href}
																	className={navItemClass(team.current)}
																>
																	<span className='flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border border-ink-700 bg-ink-800 text-[0.625rem] font-medium text-ink-400 group-hover:text-primary-400'>
																		{team.initial}
																	</span>
																	<span className='truncate'>{team.name}</span>
																</NavLink>
															</li>
														))}
													</ul>
												</li>
												<li className='-mx-6 mt-auto'>
													<NavLink
														to='/me'
														className='flex items-center gap-x-4 px-6 py-3 text-sm font-semibold leading-6 text-white hover:bg-ink-800'
													>
														<img
															className='h-8 w-8 rounded-full bg-ink-800 ring-2 ring-primary-500/40'
															src='https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80'
															alt=''
														/>
														<span className='sr-only'>Your profile</span>
														<span aria-hidden='true'>{user?.name}</span>
													</NavLink>
												</li>
											</ul>
										</nav>
									</div>
								</Dialog.Panel>
							</Transition.Child>
						</div>
					</Dialog>
				</Transition.Root>

				{/* Static sidebar for desktop */}
				<div className='z-50 hidden xl:fixed xl:inset-y-0 xl:z-50 xl:flex xl:w-72 xl:flex-col'>
					{/* Sidebar component, swap this element with another sidebar if you like */}
					<div className='flex grow flex-col gap-y-5 overflow-y-auto bg-black px-6 ring-1 ring-white/10'>
						<div className='flex h-16 shrink-0 items-center'>
							<Logo />
						</div>
						<nav className='flex flex-1 flex-col'>
							<ul
								role='list'
								className='flex flex-1 flex-col gap-y-7'
							>
								<li>
									<ul
										role='list'
										className='-mx-2 space-y-1'
									>
										{navigation.map((item) => (
											<li key={item.name}>
												<NavLink
													to={item.href}
													className={navItemClass(item.current)}
												>
													<item.icon
														className={navIconClass(item.current)}
														aria-hidden='true'
													/>
													{item.name}
												</NavLink>
											</li>
										))}
									</ul>
								</li>
								<li className='-mx-6 mt-auto'>
									<NavLink
										to='/me'
										className='flex items-center gap-x-4 px-6 py-3 text-sm font-semibold leading-6 text-white hover:bg-ink-800'
									>
										<img
											className='h-8 w-8 rounded-full bg-ink-800 ring-2 ring-primary-500/40'
											src='https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80'
											alt=''
										/>
										<span className='sr-only'>Your profile</span>
										<span aria-hidden='true'>{user.name}</span>
									</NavLink>
								</li>
							</ul>
						</nav>
					</div>
				</div>

				<div className='min-h-screen bg-ink-900 xl:pl-72'>
					{/* Sticky search header */}
					<div className='sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-6 border-b border-white/10 bg-black px-4 shadow-sm sm:px-6 lg:px-8'>
						<button
							type='button'
							className='-m-2.5 p-2.5 text-white xl:hidden'
							onClick={() => setSidebarOpen(true)}
						>
							<span className='sr-only'>Open sidebar</span>
							<Bars3Icon
								className='h-5 w-5'
								aria-hidden='true'
							/>
						</button>

						<div className='flex flex-1 gap-x-4 self-stretch lg:gap-x-6'>
							<form
								className='flex flex-1'
								action='#'
								method='GET'
							>
								<label
									htmlFor='search-field'
									className='sr-only'
								>
									Search
								</label>
								<div className='relative w-full'>
									<MagnifyingGlassIcon
										className='pointer-events-none absolute inset-y-0 left-0 h-full w-5 text-ink-500'
										aria-hidden='true'
									/>
									<input
										id='search-field'
										className='block h-full w-full border-0 bg-transparent py-0 pl-8 pr-0 text-white placeholder:text-ink-500 focus:ring-0 sm:text-sm'
										placeholder='Search...'
										type='search'
										name='search'
									/>
								</div>
							</form>
						</div>
					</div>

					<main>
						{/* Activity list */}
						<div className='border-t border-white/10 pt-11 overflow-scroll'>
							<Outlet />
						</div>
					</main>
				</div>
			</div>
		</>
	);
}
